import { Icon } from "@iconify/react";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import countryCodes from "../../CountryCode/CountryCode.json";
import AuthUser from "../../Hooks/authUser";

const DoctorAddNewPatient = () => {
  const { userInfo } = AuthUser();
  const [password, setPassword] = useState("");
  const [phone, setPhone] = useState("");

  const [countryCode, setCountryCode] = useState(
    countryCodes[0]?.dial_code || ""
  );

  const createUser = async (user) => {
    try {
      const response = await fetch("https://servercareseebd.careseebd.com/api/v1/user/signup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(user),
      });

      if (!response.ok) {
        const responseData = await response.json();
        throw new Error(responseData.message || "Failed to create Patient.");
      }

      toast.success("Patient created successfully!");
    } catch (error) {
      toast.error(error.message || "An error occurred.");
    }
  };

  const handelSubmit = async (e) => {
    e.preventDefault();

    const name = e.target.name.value;
    const email = e.target.email.value;
    const phoneWithCountryCode = `${countryCode}${phone}`;
    if (!password) {
      toast.error("Please provide or generate a password");
      return;
    }

    const user = {
      name,
      email,
      password,
      phone: phoneWithCountryCode,
      confirmPassword: password,
      role: "user",
      referralId: userInfo._id,
      referralName: userInfo.name,
      referralEmail: userInfo.email,
      referralPhone: userInfo.phone,
    };

    try {
      await createUser(user);
      setPassword("");
      setPhone("");
      e.target.reset(); // Reset the form
    } catch (error) {
      console.error("Error creating user:", error);
    }
  };

  const generatePassword = (event) => {
    event.preventDefault();
    const chars =
      "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let generatedPassword = "";
    for (let i = 0; i < 8; i++) {
      generatedPassword += chars[Math.floor(Math.random() * chars.length)];
    }
    setPassword(generatedPassword);
  };

  return (
    <div className="space-y-6 w-full animate-fade-in">
      {/* Header Panel */}
      <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm flex flex-col sm:flex-row justify-between sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <Link
            to="/doctorDashboard/patients"
            className="p-2.5 border border-slate-200 text-slate-500 hover:text-secondary hover:border-secondary hover:bg-slate-50 rounded-xl transition duration-150"
            title="Back to Patients Directory"
          >
            <Icon icon="solar:alt-arrow-left-linear" className="text-xl" />
          </Link>
          <div>
            <h1 className="text-lg font-bold text-slate-800 tracking-tight flex items-center gap-2">
              <span>Register New Patient</span>
            </h1>
            <p className="text-xs text-slate-400 font-medium mt-0.5">
              Add a new patient user account to start therapy training sessions.
            </p>
          </div>
        </div>
      </div>

      {/* Main Registration Form Card */}
      <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <form onSubmit={handelSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Name input */}
            <div className="relative">
              <Icon
                icon="solar:user-linear"
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg"
              />
              <input
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-sm font-medium text-slate-700"
                type="text"
                name="name"
                required
                placeholder="Patient Full Name"
              />
            </div>

            {/* Email input */}
            <div className="relative">
              <Icon
                icon="solar:letter-linear"
                className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg"
              />
              <input
                className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-secondary transition text-sm font-medium text-slate-700"
                type="email"
                name="email"
                required
                placeholder="Email Address"
              />
            </div>

            {/* Phone + Country code segment */}
            <div className="flex items-stretch border border-slate-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-secondary transition">
              <div className="flex items-center pl-3.5 text-slate-400">
                <Icon icon="solar:phone-linear" className="text-lg" />
              </div>
              <select
                className="pl-2 pr-1 py-2.5 bg-white focus:outline-none text-xs font-bold text-slate-600 border-r border-slate-150 max-w-[100px] truncate"
                value={countryCode}
                onChange={(e) => setCountryCode(e.target.value)}
              >
                {countryCodes.map((country) => (
                  <option key={country.code} value={country.dial_code}>
                    {country.code} ({country.dial_code})
                  </option>
                ))}
              </select>
              <input
                className="flex-1 px-3 py-2.5 focus:outline-none text-sm font-medium text-slate-700"
                type="number"
                name="phone"
                required
                placeholder="Phone Number"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </div>

            {/* Password input with generator */}
            <div className="relative flex items-stretch border border-slate-200 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-secondary transition">
              <div className="flex items-center pl-3.5 text-slate-400">
                <Icon icon="solar:lock-password-linear" className="text-lg" />
              </div>
              <input
                type="text"
                value={password}
                name="password"
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Type or generate password"
                className="flex-1 px-3 py-2.5 focus:outline-none text-sm font-medium text-slate-700"
              />
              <button
                type="button"
                onClick={generatePassword}
                className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 border-l border-slate-200 text-slate-650 font-bold text-xs transition duration-150"
              >
                Generate
              </button>
            </div>
          </div>

          {/* Submit Button wrapper */}
          <div className="pt-2 flex justify-end">
            <button
              type="submit"
              className="bg-secondary hover:bg-secondary/95 text-white font-bold py-2.5 px-6 rounded-xl shadow-md shadow-indigo-900/10 hover:shadow-lg transition duration-200 hover:-translate-y-0.5 active:translate-y-0 text-sm flex items-center gap-1.5"
            >
              <Icon icon="solar:user-plus-bold" className="text-base" />
              <span>Add New Patient</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DoctorAddNewPatient;
